import React, { useState, useContext, useEffect } from 'react';
import { ErpContext } from '../../context/ErpContext.js';
import { useParams } from 'react-router-dom';
import { useTenantNavigation } from '../../hooks/useTenantNavigation';
import imgLutenteNav from '../../assets/images/LutenteNavTransparent.png';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonIcon from '@mui/icons-material/Person';
import { styled } from '@mui/material/styles';
import Cookies from 'js-cookie';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '450px',
  margin: 'auto',
  textAlign: 'center',
  boxShadow: theme.shadows[10],
  borderRadius: '16px',
  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
}));

const ClienteLoginComponent = () => {
  const { tenant } = useParams();
  const navigate = useTenantNavigation();
  
  const { loginCliente, tenantContext, user, logout } = useContext(ErpContext);
  const [dniCuitCuil, setDniCuitCuil] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenantActual, setTenantActual] = useState('');

  // Establecer tenant
  useEffect(() => {
    const tenantToUse = tenant || Cookies.get('tenantURL') || tenantContext;
    setTenantActual(tenantToUse);
  }, [tenant, tenantContext]);

  // BLOQUEO: Si hay sesión de admin activa, cerrarla y mostrar mensaje
  useEffect(() => {
    if (user) {
      console.warn('⚠️ [ClienteLogin] Sesión de admin detectada. Cerrando sesión admin para acceder al portal de clientes.');
      logout(); // Cerrar sesión admin
    }
  }, [user, logout]);

  // Redirigir si ya está autenticado como cliente
  useEffect(() => {
    const token = localStorage.getItem('clienteToken');
    if (token) {
      // Ya tiene token de cliente, redirigir al dashboard
      navigate('mispagos/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    // Solo permitir números
    const value = e.target.value.replace(/\D/g, '');
    setDniCuitCuil(value);
    setError(''); // Limpiar error al escribir
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validaciones
    if (!dniCuitCuil) {
      setError('Por favor, ingresá tu DNI/CUIT/CUIL');
      return;
    }

    if (dniCuitCuil.length < 7 || dniCuitCuil.length > 11) {
      setError('El DNI/CUIT/CUIL debe tener entre 7 y 11 dígitos');
      return;
    }

    if (!tenantActual) {
      setError('Error: No se pudo determinar el tenant');
      return;
    }

    setLoading(true);

    try {
      const response = await loginCliente(dniCuitCuil, tenantActual);
      
      if (response.token) {
        // IMPORTANTE: Cerrar cualquier sesión admin que pueda existir
        if (user) {
          logout();
        }
        
        // Guardar token y datos del cliente
        localStorage.setItem('clienteToken', response.token);
        localStorage.setItem('clienteData', JSON.stringify(response.cliente));
        
        // Redirigir al dashboard
        navigate('mispagos/dashboard');
      }
    } catch (err) {
      console.error('Error en login cliente:', err);
      
      // Manejar diferentes tipos de errores
      if (err.response?.data?.code === 'RATE_LIMIT_EXCEEDED') {
        setError('Demasiados intentos. Por favor, esperá 15 minutos e intentá nuevamente.');
      } else if (err.response?.status === 404) {
        setError('No encontramos ningún registro con ese DNI/CUIT/CUIL. Verificá que sea correcto.');
      } else {
        setError(err.response?.data?.message || 'Error al iniciar sesión. Por favor, intentá nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <StyledPaper elevation={12}>
        <Box display="flex" justifyContent="center" mb={2}>
          <img 
            className="imgLutenteNav titleLutenteNav" 
            src={imgLutenteNav} 
            alt="Lutente" 
            style={{ maxWidth: '180px' }}
          />
        </Box>
        
        <Typography variant="h5" component="h1" mb={1} fontWeight="bold" color="primary">
          Portal de Clientes
        </Typography>
        
        <Typography variant="body2" color="text.secondary" mb={3}>
          Consultá el estado de tus cuotas
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            label="DNI / CUIT / CUIL"
            name="dniCuitCuil"
            variant="outlined"
            fullWidth
            margin="normal"
            value={dniCuitCuil}
            onChange={handleChange}
            required
            disabled={loading}
            placeholder="Ej: 12345678"
            inputProps={{ 
              maxLength: 11,
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            InputProps={{
              startAdornment: (
                <PersonIcon sx={{ color: 'action.active', mr: 1 }} />
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
              }
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LockOpenIcon />}
            sx={{ 
              marginTop: '1.5rem', 
              fontWeight: 'bold',
              padding: '12px',
              borderRadius: '12px',
              fontSize: '1rem',
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(91, 91, 95, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(91, 91, 95, 0.4)',
              }
            }}
          >
            {loading ? 'Ingresando...' : 'Ingresar'}
          </Button>
        </form>

        <Box mt={3}>
          <Typography variant="caption" color="text.secondary">
            Al ingresar, podrás consultar el estado de tus pagos y cuotas.
          </Typography>
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            ¿Problemas para ingresar? Contactanos.
          </Typography>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default ClienteLoginComponent;

